import { SampleFilterMode } from 'src/app/enums/sampleFilterMode.enum';
import { SampleShiftCoverage } from 'src/app/enums/sampleShiftType.enum';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import {
    RiskAssessmentSurveyTypeFilter,
    SampleFilterDimension,
} from 'src/app/models/risk-assessment.model';
import { SampleFilterOption } from 'src/app/common-components/sample-filter-dimension/sample-filter-dimension.component';

/**
 * G19 RA sample-filter helpers shared by the create/edit dialog and the RA detail meta strip. Keeps
 * the three-dimension view-model wiring, the survey-type row<->dimension conversion, and the summary
 * renderer in one place so the dialog's review step and the meta strip cannot drift (AC9).
 */

/** All shift-coverage values. The dimension is nullable, so (None) is offered separately. */
export const SHIFT_COVERAGE_OPTIONS: SampleFilterOption[] = [
    { id: SampleShiftCoverage.FullShift, name: 'Full Shift' },
    { id: SampleShiftCoverage.PartialShift, name: 'Partial Shift' },
];

/**
 * All sample-type values. The full set is passed to the control on purpose: the BE's
 * `ExcludesEverything` guard counts `Enum.GetValues(SampleType).Length`, so the FE must offer every
 * value (see the control's FE-2 contract note). The dimension is non-nullable — no (None).
 */
export const SAMPLE_TYPE_OPTIONS: SampleFilterOption[] = [
    { id: SampleTypeEnum.Air, name: 'Air' },
    { id: SampleTypeEnum.Noise, name: 'Noise' },
    { id: SampleTypeEnum.Wipe, name: 'Wipe' },
    { id: SampleTypeEnum.Biological, name: 'Biological' },
    { id: SampleTypeEnum.Other, name: 'Other' },
    { id: SampleTypeEnum.Vibration, name: 'Vibration' },
];

/** A fresh "no filtering" dimension (All mode). */
export function emptyDimension<T>(): SampleFilterDimension<T> {
    return { mode: SampleFilterMode.All, values: [], noneSelected: false };
}

/**
 * Collapse the GUID-keyed survey-type row set into the uniform three-mode dimension the shared control
 * consumes. `surveyTypeId === null` is the first-class (None)/unspecified bucket. An empty/undefined
 * row set is "no filtering" (All). Mode is derived from the rows: every row excluded => AllExcept,
 * otherwise OnlyThese (the API never mixes include/exclude within one dimension).
 */
export function surveyRowsToDimension(
    rows: RiskAssessmentSurveyTypeFilter[] | null | undefined,
): SampleFilterDimension<string> {
    if (!rows || rows.length === 0) {
        return emptyDimension<string>();
    }
    const mode = rows.every(r => r.isExcluded) ? SampleFilterMode.AllExcept : SampleFilterMode.OnlyThese;
    const values = rows
        .filter(r => r.surveyTypeId != null)
        .map(r => r.surveyTypeId as string);
    const noneSelected = rows.some(r => r.surveyTypeId == null);
    return { mode, values, noneSelected };
}

/**
 * Expand the survey-type dimension back into the wire row set. All => no rows. Each selected value
 * becomes a row carrying the dimension's include/exclude sense; the (None) bucket is a `surveyTypeId:
 * null` row. On update an empty array is a destructive clear (BE-3) — an All dimension intentionally
 * resets survey filtering.
 */
export function dimensionToSurveyRows(
    dim: SampleFilterDimension<string> | null | undefined,
): RiskAssessmentSurveyTypeFilter[] {
    if (!dim || dim.mode === SampleFilterMode.All) {
        return [];
    }
    const isExcluded = dim.mode === SampleFilterMode.AllExcept;
    const rows: RiskAssessmentSurveyTypeFilter[] = (dim.values ?? []).map(id => ({
        surveyTypeId: id,
        isExcluded,
    }));
    if (dim.noneSelected) {
        rows.push({ surveyTypeId: null, isExcluded });
    }
    return rows;
}

/** Human-readable one-line summary of a single dimension, using the caller's translate function. */
export function summarizeDimension(
    dim: SampleFilterDimension<number | string> | null | undefined,
    options: SampleFilterOption[],
    tt: (key: string) => string,
    allLabel: string,
    noneLabel = 'None',
): string {
    if (!dim || dim.mode === SampleFilterMode.All) {
        return tt(allLabel);
    }
    const names = (dim.values ?? []).map(id => {
        const match = options.find(o => o.id === id);
        return match ? tt(match.name) : tt('Unknown');
    });
    if (dim.noneSelected) {
        names.push(tt(noneLabel));
    }
    const prefix = dim.mode === SampleFilterMode.AllExcept ? tt('All except') : tt('Only these');
    const joined = names.length ? names.join(', ') : tt('None');
    return `${prefix}: ${joined}`;
}

/**
 * The three-dimension summary rows shared by the dialog review step and the RA detail meta strip.
 * `surveyTypeFilters` is the GUID-keyed row set (converted internally); pass the survey-type lookup so
 * ids render as names.
 */
export function buildSampleFilterSummaryRows(
    sampleFilters: { shiftCoverage?: SampleFilterDimension<number>; sampleType?: SampleFilterDimension<number> } | null | undefined,
    surveyTypeFilters: RiskAssessmentSurveyTypeFilter[] | null | undefined,
    surveyOptions: SampleFilterOption[],
    tt: (key: string) => string,
): { label: string; value: string }[] {
    return [
        {
            label: tt('Shift Coverage'),
            value: summarizeDimension(sampleFilters?.shiftCoverage, SHIFT_COVERAGE_OPTIONS, tt, 'All shift types'),
        },
        {
            label: tt('Sample Type'),
            value: summarizeDimension(sampleFilters?.sampleType, SAMPLE_TYPE_OPTIONS, tt, 'All sample types'),
        },
        {
            label: tt('Survey Type'),
            value: summarizeDimension(surveyRowsToDimension(surveyTypeFilters), surveyOptions, tt, 'All survey types'),
        },
    ];
}
